import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useSession } from '@/features/auth/session';
import { useUnclaimedCount } from '@/features/queue/queries';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Mark } from './mark';
import { ThemeToggle } from './theme-toggle';

/* The chassis. A hairline rail of sections on the left, the work on the right —
   the same shape as the operator console, because these are two views of one
   product and an operator and an expert often sit beside each other.

   Two sections only. An expert does one job: work the queue. Projects is there
   because a reviewer needs to know which repos a project holds before reading a
   multi-repo diff, not because it is a second workflow. */

const SECTIONS: { label: string; items: { to: string; label: string; badge?: 'queue' }[] }[] = [
  {
    label: 'Review',
    items: [
      { to: '/queue', label: 'Review queue', badge: 'queue' },
      { to: '/projects', label: 'Your projects' },
    ],
  },
];

export function Shell() {
  const { expert, signOut } = useSession();
  const navigate = useNavigate();
  const unclaimed = useUnclaimedCount();

  const badgeFor = (badge?: 'queue') => (badge === 'queue' && unclaimed && unclaimed > 0 ? unclaimed : null);

  return (
    <div className="flex min-h-screen">
      <nav
        className="sticky top-0 hidden h-screen w-[216px] shrink-0 flex-col border-r border-rule bg-surface md:flex"
        aria-label="Console sections"
      >
        <div className="flex items-center gap-2.5 border-b border-rule px-4 py-3.5">
          <Mark className="h-5 w-5 shrink-0 text-ink" />
          <div className="min-w-0">
            <p className="text-lg font-semibold leading-none tracking-[-0.02em] text-ink">hourz</p>
            <p className="eyebrow mt-1">Review console</p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto py-2">
          {SECTIONS.map((section) => (
            <div key={section.label} className="mb-1 px-2 py-1.5">
              <p className="eyebrow px-2 pb-1">{section.label}</p>
              <ul className="m-0 list-none p-0">
                {section.items.map((item) => {
                  const badge = badgeFor(item.badge);
                  return (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        className={({ isActive }) =>
                          cn(
                            'relative flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors',
                            isActive ? 'bg-sunken font-medium text-ink' : 'text-ink-2 hover:bg-sunken hover:text-ink',
                          )
                        }
                      >
                        {({ isActive }) => (
                          <>
                            {/* The active marker is a tick on the rail, the same
                                language the Run Ledger uses. */}
                            <span
                              aria-hidden
                              className={cn(
                                'absolute left-[-8px] top-1/2 h-3.5 w-[2px] -translate-y-1/2 rounded-sm transition-colors',
                                isActive ? 'bg-action' : 'bg-transparent',
                              )}
                            />
                            <span className="min-w-0 truncate">{item.label}</span>
                            {badge ? (
                              <span
                                className="ml-auto shrink-0 font-mono text-tiny tabular-nums text-warn"
                                title={`${badge} chat${badge === 1 ? '' : 's'} waiting, unclaimed`}
                              >
                                {badge}
                              </span>
                            ) : null}
                          </>
                        )}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-rule px-3 py-2.5">
          <div className="mb-2 min-w-0">
            <p className="truncate text-sm font-medium text-ink">{expert?.display_name ?? '—'}</p>
            <p className="truncate font-mono text-tiny text-ink-3">{expert?.email ?? ''}</p>
          </div>
          <div className="flex items-center justify-between gap-2">
            <ThemeToggle />
            <Button
              size="sm"
              variant="ghost"
              onClick={async () => {
                await signOut();
                navigate('/login', { replace: true });
              }}
            >
              Sign out
            </Button>
          </div>
        </div>
      </nav>

      {/* Narrow viewports get the same sections as a strip rather than a
          hamburger: a reviewer on a laptop should never have to open a menu to
          find the queue. */}
      <div className="flex min-w-0 flex-1 flex-col">
        <nav className="sticky top-0 z-30 flex items-center gap-1 overflow-x-auto border-b border-rule bg-surface px-3 py-2 md:hidden">
          <Mark className="mr-1 h-4 w-4 shrink-0 text-ink" />
          {SECTIONS.flatMap((s) => s.items).map((item) => {
            const badge = badgeFor(item.badge);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    'flex shrink-0 items-center gap-1.5 rounded-sm px-2 py-1 text-xs',
                    isActive ? 'bg-sunken font-medium text-ink' : 'text-ink-2',
                  )
                }
              >
                {item.label}
                {badge ? <span className="font-mono text-tiny tabular-nums text-warn">{badge}</span> : null}
              </NavLink>
            );
          })}
          <div className="ml-auto shrink-0">
            <ThemeToggle />
          </div>
        </nav>

        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
